import { useContext, useState, useEffect, useRef } from 'react';
import {
  ClearAllButton,
  DropdownStyled,
  ExpertWidgetListItem,
  ExpertsContainer,
  FindExpertImageContainer,
  FindExpertTitle,
  SkillExpertsList,
  SkillsFindExpertContainer,
  SkillsFindExpertContent,
  StyledImage,
  ExpertActionsContainer,
  MobileCallUserPopup,
  MobileCallUserOverlay,
  PlaceCallActionsContainer,
  PlaceCallTextContainer,
} from './style/SkillsWidget.style';
import { Option } from '@fluentui/react-components';
import findExpertImage from 'src/assets/skillsFindExpertImage.png';
import expertsNotFoundImage from 'src/assets/skillsExpertsNotFound.png';
import {
  OptionSet,
  Skill,
  EmployeeSkill,
} from 'src/tabs/models/SkillsInterfaces';
import { TeamWidgetPersona } from 'src/components/Timeoff/widgets/TeamWidget/styles/TeamWidgetSection.styled';
import {
  filterCategoriesByKeyWord,
  getCategoryValue,
} from '../utils/filterCategories';
import {
  filterSkillsByCategory,
  filterSkillsByKeyWord,
} from '../utils/filterSkills';
import { TeamsFxContext } from 'src/context/TeamsContext';
import {
  avatarBackgroundColors,
  avatarColors,
} from 'src/components/Timeoff/TeamVacationCircle/styles/TeamVacationCircle.styled';
import {
  Call24Filled,
  Chat24Filled,
  Chat24Regular,
  Call24Regular,
} from '@fluentui/react-icons';
import {
  openChatWithUser,
  callUserDirectly,
} from './utils/teamsContactEmployee';
import {
  ComboboxStyle,
  OptionsWrapper,
} from '../SkillsAddPopup/styles/SkillsAddPopup.styled';
import { ExpertContainerMobile } from './mobile/ExpertContainerMobile';
import {
  PrimaryButton,
  SecondaryButton,
} from 'src/styles/globalStyles/buttonStyles.styled';

interface SkillsWidgetsProps {
  categories: OptionSet[];
  expertEmployees: EmployeeSkill[];
  getExperts: (skillId: string) => Promise<void>;
  skillsByCategory: Skill[];
  clearExperts: () => void;
  loadingExperts: boolean;
  handleCategoryChange: (category: string) => void;
  setFindExpertPopup?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const SkillsWidgets = ({
  categories,
  expertEmployees,
  getExperts,
  skillsByCategory,
  clearExperts,
  loadingExperts,
  handleCategoryChange,
  setFindExpertPopup,
}: SkillsWidgetsProps) => {
  const dropDownsRef = useRef(null);
  const [dropDownWidth, setDropDownWidth] = useState();
  const [isExpertIconChatHovered, setIsExpertIconChatHovered] = useState(-1);
  const [isExpertIconCallHovered, setIsExpertIconCallHovered] = useState(-1);
  const [isCallUserPopupVisible, setisCallUserPopupVisible] = useState(false);
  const [expertToCall, setExpertToCall] = useState({ name: '', email: '' });

  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const { appHost, isMobile } = useContext(TeamsFxContext);
  const [searchSkillInput, setSearchSkillInput] = useState('');
  const [searchCategoryInput, setSearchCategoryInput] = useState('');
  const [selectedSkill, setSelectedSkill] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const categoryValue = getCategoryValue(categories, selectedCategory);

  const filteredSkillsByCategory: Skill[] = filterSkillsByCategory(
    skillsByCategory,
    +categoryValue
  );

  const filteredCategoriesByKeyWord =
    searchCategoryInput && !selectedCategory
      ? filterCategoriesByKeyWord(categories, searchCategoryInput)
      : categories;

  const filteredSkillsByKeyWord =
    searchSkillInput && !selectedSkill
      ? filterSkillsByKeyWord(skillsByCategory, searchSkillInput)
      : filteredSkillsByCategory;

  const foundExperts = expertEmployees.length > 0;

  const isClearAllVisible =
    foundExperts || selectedCategory || selectedSkill || searchSkillInput;

  const expertsNotFound =
    expertEmployees.length === 0 && selectedSkill && !loadingExperts;

  const personasExpertsArray = expertEmployees.map((expertPersona, index) => {
    if (expertEmployees.length <= avatarBackgroundColors.length) {
      return {
        ...expertPersona,
        backgroundPersona: avatarBackgroundColors[index],
        colorPersona: avatarColors[index],
      };
    }

    return {
      ...expertPersona,
      backgroundPersona:
        avatarBackgroundColors[index % avatarBackgroundColors.length],
      colorPersona: avatarColors[index % avatarColors.length],
    };
  });

  const onClear = () => {
    clearExperts();
    setSearchCategoryInput('');
    setSelectedSkill('');
    setSelectedCategory('');
    handleCategoryChange('');
    setSearchSkillInput('');
  };

  useEffect(() => {
    document.addEventListener('keydown', detectKeyDown, true);
    const element = dropDownsRef.current;
    if (element) {
      setDropDownWidth(element.getBoundingClientRect().width);
    }
    const handleResize = () => {
      const newWindowSize = {
        width: window.innerWidth,
        height: window.innerHeight,
      };
      setWindowSize(newWindowSize);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [windowSize]);

  const detectKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.stopImmediatePropagation();
    }
  };

  const handleMobileCallUserPopup = () => {
    setisCallUserPopupVisible(false);
    setIsExpertIconCallHovered(-1);
  };

  const handleStartCall = () => {
    callUserDirectly(expertToCall.email);
    setIsExpertIconCallHovered(-1);
  };

  const handleFindExpertOnChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setSearchCategoryInput(value);
    setSearchSkillInput('');
    setSelectedSkill('');
    if (selectedCategory) {
      setSelectedCategory('');
    }
  };

  const handleFilterOptionClick = (category: OptionSet) => {
    setSelectedCategory(category.label);
    setSearchCategoryInput(category.label);
    handleCategoryChange(category.value);
    setSearchSkillInput('');
    setSelectedSkill('');
  };
  const handleSkillOptionClick = (skill: Skill) => {
    setSearchSkillInput(skill.name);
    setSelectedSkill(skill.name);
    getExperts(skill.id);
  };

  const handleDropDownComboChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    {
      const value = event.target.value;
      setSearchSkillInput(value);
      if (selectedSkill) {
        setSelectedSkill('');
      }
    }
  };

  const handleExpertIconChatClick = (
    employee: EmployeeSkill,
    index: number
  ) => {
    if (isMobile) {
      setIsExpertIconChatHovered(index);
    }
    openChatWithUser(employee.employee.cra0e_email, '');
  };

  const handleExpertIconCallClick = (
    employee: EmployeeSkill,
    index: number
  ) => {
    if (isMobile) {
      setIsExpertIconCallHovered(index);
      setExpertToCall({
        name: employee.employee.new_name,
        email: employee.employee.cra0e_email,
      });
      setisCallUserPopupVisible(true);
      return;
    }
    callUserDirectly(employee.employee.cra0e_email);
  };
  return (
    <>
      {isCallUserPopupVisible && (
        <MobileCallUserPopup onClick={handleMobileCallUserPopup} />
      )}
      {isCallUserPopupVisible && (
        <MobileCallUserOverlay>
          <PlaceCallTextContainer>
            <h1>Start call?</h1>
            <p>Place a call to {expertToCall.name}</p>
          </PlaceCallTextContainer>
          <PlaceCallActionsContainer>
            <SecondaryButton onClick={handleMobileCallUserPopup}>
              Cancel
            </SecondaryButton>
            <PrimaryButton onClick={handleStartCall}>Start Call</PrimaryButton>
          </PlaceCallActionsContainer>
        </MobileCallUserOverlay>
      )}
      <SkillsFindExpertContainer>
        {isMobile ? (
          <ExpertContainerMobile
            isClearAllVisible={isClearAllVisible}
            onClear={onClear}
            setFindExpertPopup={setFindExpertPopup}
          />
        ) : (
          <FindExpertTitle>
            Find an expert
            {isClearAllVisible && (
              <ClearAllButton onClick={onClear}>Clear all</ClearAllButton>
            )}
          </FindExpertTitle>
        )}
        <SkillsFindExpertContent>
          <DropdownStyled ref={dropDownsRef} id="dropdown-id">
            <ComboboxStyle
              appearance="filled-darker"
              placeholder="Categories"
              onChange={(event) => handleFindExpertOnChange(event)}
              listbox={{
                style: {
                  maxHeight: '250px',
                  width: `${dropDownWidth}px`,
                  fontSize: '16px',
                },
              }}
              value={searchCategoryInput}
            >
              <OptionsWrapper>
                {filteredCategoriesByKeyWord.map((category) => (
                  <Option
                    key={category.value}
                    onClick={() => handleFilterOptionClick(category)}
                    checkIcon={{
                      style: {
                        color: '#5B5FC7',
                        opacity: selectedCategory === category.label ? 1 : 0,
                      },
                    }}
                  >
                    {category.label}
                  </Option>
                ))}
              </OptionsWrapper>
            </ComboboxStyle>
          </DropdownStyled>
          <DropdownStyled id="dropdown-id">
            <ComboboxStyle
              appearance="filled-darker"
              placeholder="Skill"
              onChange={(event) => handleDropDownComboChange(event)}
              listbox={{
                style: {
                  maxHeight: '250px',
                  width: `${dropDownWidth}px`,
                  fontSize: '16px',
                },
              }}
              value={searchSkillInput}
            >
              <OptionsWrapper>
                {filteredSkillsByKeyWord.map((skill) => (
                  <Option
                    key={skill.id}
                    onClick={() => handleSkillOptionClick(skill)}
                    checkIcon={{
                      style: {
                        color: '#5B5FC7',
                        opacity: selectedSkill === skill.id ? 1 : 0,
                      },
                    }}
                  >
                    {skill.name}
                  </Option>
                ))}
              </OptionsWrapper>
            </ComboboxStyle>
          </DropdownStyled>
          {foundExperts && selectedSkill && (
            <SkillExpertsList appHost={appHost}>
              <ExpertsContainer>
                {personasExpertsArray.map((employee, index) => {
                  const ExpertIconChat =
                    isExpertIconChatHovered === index
                      ? Chat24Filled
                      : Chat24Regular;

                  const ExpertIconCall =
                    isExpertIconCallHovered === index
                      ? Call24Filled
                      : Call24Regular;

                  return (
                    <ExpertWidgetListItem key={employee.id}>
                      <TeamWidgetPersona
                        text={employee.employee.new_name}
                        showOverflowTooltip={false}
                        backgroundPersonal={employee.backgroundPersona}
                        colorPersona={employee.colorPersona}
                      />
                      <ExpertActionsContainer>
                        <ExpertIconChat
                          color={`${
                            isExpertIconChatHovered === index && '#5B5FC7'
                          }`}
                          onMouseEnter={() =>
                            !isMobile && setIsExpertIconChatHovered(index)
                          }
                          onMouseLeave={() =>
                            !isMobile && setIsExpertIconChatHovered(-1)
                          }
                          onClick={() =>
                            handleExpertIconChatClick(employee, index)
                          }
                        />
                        <ExpertIconCall
                          color={`${
                            isExpertIconCallHovered === index && '#5B5FC7'
                          }`}
                          onMouseEnter={() =>
                            !isMobile && setIsExpertIconCallHovered(index)
                          }
                          onMouseLeave={() =>
                            !isMobile && setIsExpertIconCallHovered(-1)
                          }
                          onClick={() =>
                            handleExpertIconCallClick(employee, index)
                          }
                        />
                      </ExpertActionsContainer>
                    </ExpertWidgetListItem>
                  );
                })}
              </ExpertsContainer>
            </SkillExpertsList>
          )}
          {!foundExperts && !selectedSkill && (
            <FindExpertImageContainer>
              <StyledImage src={findExpertImage} alt="Find an expert" />
              <h1>Find a skill expert</h1>
              <p>
                {selectedCategory
                  ? 'Please select the desired skill'
                  : 'Get started by selecting skill category'}
              </p>
            </FindExpertImageContainer>
          )}
          {expertsNotFound && (
            <FindExpertImageContainer>
              <StyledImage src={expertsNotFoundImage} alt="404" />
              <h1>No Results Found</h1>
              <p>Try choosing another skill or category.</p>
            </FindExpertImageContainer>
          )}
        </SkillsFindExpertContent>
      </SkillsFindExpertContainer>
    </>
  );
};
